/**
 * ChestItem entity: a treasure chest placed in the maze.
 *
 * Chests come in three tiers (metallic, copper, gold) and require
 * a matching-tier (or higher) key to open. Chests persist until
 * opened — they never despawn on their own.
 *
 * Features:
 *  - Static placement at maze generation time
 *  - Open animation with light burst, zoom, and shake
 *  - Gold chests produce a brief screen flash
 *  - Loot reveal after opening (heart, star, coins, agmuth, or empty)
 */

import Phaser from 'phaser';
import {
  CHEST_TEXTURE_MAP,
  ChestTier,
  TEX_GLOW,
} from '../utils/constants.js';
import {
  TILE_SIZE,
  AUDIO_CHEST_OPEN,
  CHEST_OPEN_SHAKE_INTENSITY,
  CHEST_OPEN_SHAKE_DURATION,
  CHEST_OPEN_LIGHT_DURATION,
  CHEST_GOLD_FLASH_DURATION,
  COLOR_METALLIC,
  COLOR_COPPER,
  COLOR_GOLD,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

/** @type {Object<string, number>} Tier to glow tint color */
const TIER_COLOR = Object.freeze({
  [ChestTier.METALLIC]: COLOR_METALLIC,
  [ChestTier.COPPER]: COLOR_COPPER,
  [ChestTier.GOLD]: COLOR_GOLD,
});

export default class ChestItem extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x - World X position (center of cell)
   * @param {number} y - World Y position (center of cell)
   * @param {string} tier - One of ChestTier values
   * @param {number} gridCol - Grid column
   * @param {number} gridRow - Grid row
   */
  constructor(scene, x, y, tier, gridCol, gridRow) {
    const texKey = CHEST_TEXTURE_MAP[tier] || CHEST_TEXTURE_MAP[ChestTier.METALLIC];
    super(scene, x, y, texKey);

    /** @type {string} */
    this.tier = tier;

    /** @type {number} */
    this.gridCol = gridCol;
    /** @type {number} */
    this.gridRow = gridRow;

    /** @type {boolean} Whether this chest has been opened. */
    this.isOpen = false;

    scene.add.existing(this);
    this.setDepth(3);

    // No physics body — chests are opened via manual adjacency check, not overlap
  }

  /**
   * Play the chest open animation sequence.
   * Returns a Promise that resolves after the animation finishes
   * and the chest is ready for loot display.
   *
   * @param {boolean} isGold - Whether to play enhanced gold-tier effects
   * @returns {Promise<void>}
   */
  open(isGold = false) {
    if (this.isOpen) return Promise.resolve();
    this.isOpen = true;

    return new Promise((resolve) => {
      const scene = this.scene;
      if (!scene) {
        resolve();
        return;
      }

      // Play chest open sound (gracefully handle missing audio)
      try {
        scene.sound.play(AUDIO_CHEST_OPEN, { volume: getSfxVol(0.6) });
      } catch (_) { /* audio file may not exist yet */ }

      // --- Camera shake (subtle) ---
      scene.cameras.main.shake(
        CHEST_OPEN_SHAKE_DURATION,
        CHEST_OPEN_SHAKE_INTENSITY,
      );

      // --- Light burst from the chest ---
      const color = TIER_COLOR[this.tier] || COLOR_METALLIC;
      const lightGfx = scene.add.graphics();
      lightGfx.setDepth(150);
      lightGfx.fillStyle(color, 0.6);
      lightGfx.fillCircle(this.x, this.y, 4);
      lightGfx.fillStyle(0xffffff, 0.3);
      lightGfx.fillCircle(this.x, this.y, 8);

      // Expand and fade the light burst
      scene.tweens.add({
        targets: lightGfx,
        scaleX: 4,
        scaleY: 4,
        alpha: 0,
        duration: CHEST_OPEN_LIGHT_DURATION,
        ease: 'Quad.easeOut',
        onComplete: () => {
          lightGfx.destroy();
        },
      });

      // --- Glow particles emanating from the chest ---
      const emitter = scene.add.particles(this.x, this.y, TEX_GLOW, {
        lifespan: { min: 300, max: 600 },
        speed: { min: 20, max: 60 },
        angle: { min: 200, max: 340 }, // Upward spread
        scale: { start: 0.5, end: 0 },
        alpha: { start: 0.8, end: 0 },
        frequency: 30,
        quantity: 3,
        tint: color,
        emitting: true,
      });
      emitter.setDepth(149);

      // Stop particles after a short burst
      scene.time.delayedCall(500, () => {
        if (emitter) emitter.stop();
      });

      // --- Gold chest: screen flash ---
      if (isGold) {
        scene.cameras.main.flash(
          CHEST_GOLD_FLASH_DURATION,
          255, 215, 0, // gold-ish flash
          false, // don't force
        );
      }

      // --- Chest sprite: scale up slightly then shrink and fade ---
      scene.tweens.add({
        targets: this,
        scaleX: 1.3,
        scaleY: 1.3,
        duration: 200,
        ease: 'Quad.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: this,
            scaleX: 0,
            scaleY: 0,
            alpha: 0,
            duration: 400,
            ease: 'Quad.easeIn',
            onComplete: () => {
              // Clean up particles
              if (emitter) emitter.destroy();
              // Don't destroy the chest yet — GameScene handles final cleanup
              resolve();
            },
          });
        },
      });
    });
  }

  /**
   * Clean up resources.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    super.destroy(fromScene);
  }
}
